import * as React from "react";
import { useSelector } from "@xstate/react";
import { css } from "@emotion/core";
import {
  Button,
  Callout,
  Card,
  CardList,
  CompoundTag,
  Switch,
} from "@blueprintjs/core";
import {
  formatBytes,
  formatDuration,
  formatTimeOnly,
} from "../../../utils/format";
import { usePreventSleep } from "hooks/";
import { useUploadEstimate } from "../hooks";
import {
  useUploadMachine,
  useUploadMachineSelector,
} from "../context/upload-machine";
import { OrderMachineContext } from "../state/orderMachine";
import type {
  FileUploadActor,
  FileUploadInput,
} from "../state/uploadMachine/types";

const getFileExtension = (filename: string): string => {
  return filename.split(".").pop()?.toLowerCase() || "";
};

const isImageFile = (filename: string): boolean => {
  const ext = getFileExtension(filename);
  return ["jpg", "jpeg", "rjpeg"].includes(ext);
};

const isLidarFile = (filename: string): boolean => {
  const ext = getFileExtension(filename);
  return ["las", "laz"].includes(ext);
};

const isGeometryFile = (filename: string): boolean => {
  const ext = getFileExtension(filename);
  return [
    "shp",
    "kml",
    "kmz",
    "geojson",
    "prj",
    "shx",
    "dbf",
    "csv",
    "pdf",
  ].includes(ext);
};

function UploadListItem({ fileUploader }: { fileUploader: FileUploadActor }) {
  const progress = useSelector(fileUploader, (state) => state.context.progress);
  const speed = useSelector(fileUploader, (state) => state.context.speed);

  return (
    <Card
      css={css`
        position: relative;
      `}
    >
      <div
        css={css`
          position: absolute;
          bottom: 0;
          left: 0;
          width: ${Math.round(progress * 100)}%;
          height: 100%;
          border-bottom: 2px solid var(--fybr-blue);
        `}
      />
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          z-index: 1;
        `}
      >
        <span>{fileUploader?.getSnapshot().context.file.name}</span>{" "}
        <div>
          <CompoundTag
            size="large"
            intent={"success"}
            minimal
            leftContent={
              <div
                css={css`
                  font-variant-numeric: tabular-nums;
                  width: 4rem;
                  text-align: right;
                `}
              >
                {Math.round(progress * 100)}%
              </div>
            }
          >
            <div
              css={css`
                width: 6rem;
                font-variant-numeric: tabular-nums;
                text-align: right;
              `}
            >
              {speed > 0 ? `${formatBytes(speed)}/s` : "-"}
            </div>
          </CompoundTag>
        </div>
      </div>
    </Card>
  );
}

function SleepPrevention() {
  const { allowSleep, preventSleep } = usePreventSleep();

  React.useEffect(() => {
    preventSleep();

    return () => {
      allowSleep();
    };
  }, [allowSleep, preventSleep]);

  return <></>;
}

export default function Uploads() {
  const uploader = useUploadMachine();

  const orderMachine = OrderMachineContext.useActorRef();
  const autoSubmit = OrderMachineContext.useSelector(
    (state) => state.context.autoSubmit
  );

  const fileUploaders = useUploadMachineSelector(
    (state) => state.context.fileUploadActors
  );

  const uploadedBytes = useUploadMachineSelector(
    (state) => state.context.uploadedBytes
  );

  const totalFiles = useUploadMachineSelector(
    (state) =>
      state.context.uploadedFiles.length +
      state.context.queuedFiles.length +
      state.context.fileUploadActors.length
  );

  const totalImageFiles = useUploadMachineSelector((state) => {
    const uploadedCount = state.context.imageUploads.length;
    const queuedCount = state.context.queuedFiles.filter((f: FileUploadInput) =>
      isImageFile(f.key)
    ).length;
    const activeCount = state.context.fileUploadActors.filter(
      (actor: FileUploadActor) => isImageFile(actor.getSnapshot().context.key)
    ).length;
    return uploadedCount + queuedCount + activeCount;
  });

  const totalLidarFiles = useUploadMachineSelector((state) => {
    const uploadedCount = state.context.lidarUploads.length;
    const queuedCount = state.context.queuedFiles.filter((f: FileUploadInput) =>
      isLidarFile(f.key)
    ).length;
    const activeCount = state.context.fileUploadActors.filter(
      (actor: FileUploadActor) => isLidarFile(actor.getSnapshot().context.key)
    ).length;
    return uploadedCount + queuedCount + activeCount;
  });

  const totalGeometryFiles = useUploadMachineSelector((state) => {
    const uploadedCount = state.context.geometryUploads.length;
    const queuedCount = state.context.queuedFiles.filter((f: FileUploadInput) =>
      isGeometryFile(f.key)
    ).length;
    const activeCount = state.context.fileUploadActors.filter(
      (actor: FileUploadActor) =>
        isGeometryFile(actor.getSnapshot().context.key)
    ).length;
    return uploadedCount + queuedCount + activeCount;
  });

  const uploadedFiles = useUploadMachineSelector(
    (state) => state.context.uploadedFiles
  );

  const uploadedImageFiles = useUploadMachineSelector(
    (state) => state.context.imageUploads
  );

  const uploadedLidarFiles = useUploadMachineSelector(
    (state) => state.context.lidarUploads
  );

  const uploadedGeometryFiles = useUploadMachineSelector(
    (state) => state.context.geometryUploads
  );

  const totalBytes = useUploadMachineSelector(
    (state) => state.context.totalBytes
  );
  const activeBytes = useUploadMachineSelector(
    (state) => state.context.activeBytes
  );

  const { etaSeconds, estimatedCompletionTime } = useUploadEstimate({
    uploadedBytes,
    activeBytes,
    totalBytes,
  });

  if (fileUploaders.length === 0) {
    return null;
  }

  return (
    <Card
      style={{
        backgroundColor: "var(--neutral-6)",
        height: "100%",
      }}
    >
      <SleepPrevention />
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          height: 100%;
          gap: 1rem;
        `}
      >
        {fileUploaders.length > 1 ? (
          <CardList
            css={css`
              flex-shrink: 0;
            `}
          >
            {fileUploaders.map((fileUploader: FileUploadActor) => (
              <UploadListItem
                key={fileUploader.id}
                fileUploader={fileUploader}
              />
            ))}
          </CardList>
        ) : (
          <UploadListItem fileUploader={fileUploaders[0]} />
        )}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
          `}
        >
          <Card>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 1rem;
                height: 100%;
                justify-content: end;
              `}
            >
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 0.5rem;
                  padding-bottom: 3rem;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding-bottom: 0.25rem;
                    border-bottom: 1px solid var(--neutral-5);
                  `}
                >
                  <span
                    css={css`
                      color: var(--neutral-3);
                    `}
                  >
                    Images
                  </span>
                  <span
                    css={css`
                      font-variant-numeric: tabular-nums;
                    `}
                  >
                    {`${uploadedImageFiles.length} / ${totalImageFiles}`}
                  </span>
                </div>
                <div
                  css={css`
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding-bottom: 0.25rem;
                    border-bottom: 1px solid var(--neutral-5);
                  `}
                >
                  <span
                    css={css`
                      color: var(--neutral-3);
                    `}
                  >
                    Point Clouds
                  </span>
                  <span
                    css={css`
                      font-variant-numeric: tabular-nums;
                    `}
                  >
                    {`${uploadedLidarFiles.length} / ${totalLidarFiles}`}
                  </span>
                </div>
                <div
                  css={css`
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding-bottom: 0.25rem;
                    border-bottom: 1px solid var(--neutral-5);
                  `}
                >
                  <span
                    css={css`
                      color: var(--neutral-3);
                    `}
                  >
                    Geometry
                  </span>
                  <span
                    css={css`
                      font-variant-numeric: tabular-nums;
                    `}
                  >
                    {`${uploadedGeometryFiles.length} / ${totalGeometryFiles}`}
                  </span>
                </div>
              </div>
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  padding-bottom: 0.25rem;
                  border-bottom: 1px solid var(--neutral-5);
                `}
              >
                <span>Total Files</span>
                <span
                  css={css`
                    font-variant-numeric: tabular-nums;
                  `}
                >
                  {`${uploadedFiles.length} / ${totalFiles}`}
                </span>
              </div>
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  padding-bottom: 0.25rem;
                  border-bottom: 1px solid var(--neutral-5);
                `}
              >
                <span>Total Bytes</span>
                <span
                  css={css`
                    font-variant-numeric: tabular-nums;
                  `}
                >
                  {`${formatBytes(uploadedBytes + activeBytes)} / ${formatBytes(
                    totalBytes
                  )}`}
                </span>
              </div>
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  padding-bottom: 0.25rem;
                  border-bottom: 1px solid var(--neutral-5);
                `}
              >
                <span>Estimated Time</span>
                <span
                  css={css`
                    font-variant-numeric: tabular-nums;
                  `}
                >
                  {estimatedCompletionTime
                    ? `${formatDuration(etaSeconds)} (around ${formatTimeOnly(
                        estimatedCompletionTime
                      )})`
                    : formatDuration(etaSeconds)}
                </span>
              </div>
            </div>
          </Card>
          <Switch
            size="large"
            checked={autoSubmit}
            onChange={() => orderMachine.send({ type: "TOGGLE_AUTO_SUBMIT" })}
            label="Automatically submit order when upload is complete"
          />
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 1rem;
            `}
          >
            <Callout intent="warning" compact>
              Please do not close this page until the upload is complete and the
              order is submitted.
            </Callout>
            <div
              css={css`
                flex-shrink: 0;
              `}
            >
              <Button
                intent="danger"
                icon="stop"
                size="large"
                onClick={() => uploader.send({ type: "CANCEL_ALL" })}
              >
                Stop Upload
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
